/**
 * AcumularSaldosDialogCxP.js — Fusionar saldos a favor de un proveedor.
 *
 * Espejo de AcumularSaldosDialog (CxC): consolida varios saldos a favor
 * (CuentaXPagar con saldo < 0) del MISMO proveedor en una sola factura destino.
 * Las demás quedan saldadas. Llama al endpoint
 * POST /api/finance/cuentaXPagar/acumular-saldos-favor/.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { overlayClose } from "../../../../shared/utils/overlayClose";

export default function AcumularSaldosDialogCxP({ negativas, onClose, onDone }) {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState(negativas[0]?.id ?? null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  // El saldo a favor se muestra como valor absoluto en la moneda de la factura
  const fmtFavor = (cxp) =>
    (cxp.moneda || "CRC").toUpperCase() === "USD"
      ? `$${Math.abs(Number(cxp.saldo)).toFixed(2)}`
      : `₡${formatCurrency(Math.abs(Number(cxp.saldo)))}`;

  // Total combinado por moneda (no se mezclan monedas en el backend)
  const totalCRC = negativas
    .filter((c) => (c.moneda || "CRC").toUpperCase() !== "USD")
    .reduce((a, c) => a + Math.abs(Number(c.saldo)), 0);
  const totalUSD = negativas
    .filter((c) => (c.moneda || "CRC").toUpperCase() === "USD")
    .reduce((a, c) => a + Math.abs(Number(c.saldo)), 0);

  async function handleConfirm() {
    if (!targetId) return;
    setSaving(true);
    setError("");
    try {
      const sources = negativas.filter((c) => c.id !== targetId).map((c) => c.id);
      const res = await apiFetch("/api/finance/cuentaXPagar/acumular-saldos-favor/", {
        method: "POST",
        body: JSON.stringify({ target: targetId, sources }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t("recibos.acumularError"));
      }
      onDone?.();
      onClose();
    } catch (e) {
      setError(e.message || t("recibos.acumularError"));
      setSaving(false);
    }
  }

  return (
    <div
      {...overlayClose(onClose)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, padding: 24, width: "min(480px, 92vw)",
          maxHeight: "85vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ background: "rgba(74,222,128,0.15)", borderRadius: 8,
            padding: "8px 10px", color: "#4ade80" }}>
            <i className="fa-solid fa-layer-group" />
          </div>
          <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
            {t("recibos.acumularTitulo")}
          </h3>
        </div>

        <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: 12 }}>
          {t("recibos.acumularElegirDestino")}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {negativas.map((cxp) => (
            <label key={cxp.id}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                border: `1px solid ${targetId === cxp.id ? "rgba(74,222,128,0.5)" : "var(--border-card)"}`,
                background: targetId === cxp.id ? "rgba(74,222,128,0.08)" : "transparent",
                borderRadius: 8, cursor: "pointer" }}>
              <input type="radio" name="target-cxp" checked={targetId === cxp.id}
                onChange={() => setTargetId(cxp.id)} />
              <span style={{ flex: 1, color: "var(--text-primary)", fontSize: "0.88rem" }}>
                {cxp.factura_cxp?.numero_doc_proveedor || cxp.factura_cxp?.numero_factura || "—"}
                <span style={{ display: "block", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                  {cxp.factura_cxp?.cuenta_proveedor?.nombre ?? ""}
                </span>
              </span>
              <span style={{ color: "#fb923c", fontWeight: 600, fontSize: "0.85rem" }}>
                {fmtFavor(cxp)}
              </span>
            </label>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-card)" }}>
          <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
            {t("recibos.acumularTotalCombinado")}
          </span>
          <span style={{ color: "#4ade80", fontWeight: 700 }}>
            {totalCRC > 0 && `₡${formatCurrency(totalCRC)}`}
            {totalCRC > 0 && totalUSD > 0 && "  ·  "}
            {totalUSD > 0 && `$${totalUSD.toFixed(2)}`}
          </span>
        </div>

        {error && (
          <div style={{ marginTop: 12, background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, padding: "8px 12px",
            color: "#f87171", fontSize: "0.82rem" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button type="button" onClick={onClose} disabled={saving}
            style={{ padding: "8px 16px", background: "var(--hover-bg)",
              border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-secondary)",
              fontWeight: 600, fontSize: "0.85rem", cursor: saving ? "not-allowed" : "pointer" }}>
            {t("recibos.acumularCancelar")}
          </button>
          <button type="button" onClick={handleConfirm} disabled={saving || !targetId}
            style={{ padding: "8px 16px", background: saving ? "rgba(74,222,128,0.5)" : "#22c55e",
              border: "none", borderRadius: 7, color: "#fff", fontWeight: 700, fontSize: "0.85rem",
              cursor: saving || !targetId ? "not-allowed" : "pointer" }}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />&nbsp;…</>
            ) : (
              <><i className="fa-solid fa-layer-group" style={{ marginRight: 6 }} />{t("recibos.acumularConfirmar")}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
